import { useCallback, useEffect, useState } from 'react';

/**
 * Shared sidebar collapse state.
 *
 * Persisted under one localStorage key so the collapsed/expanded choice is the
 * SAME across every screen — the main app layout (campaigns / home / settings)
 * and the campaign-detail surface both read and write it, so navigating between
 * them never resets the rail. Owned by the layout (not AppSidebar) so the main
 * content margin can track the rail width.
 */
const SIDEBAR_COLLAPSED_KEY = 'discovr:sidebar-collapsed';
// Below Tailwind `md` the expanded 224px rail leaves too little room for content.
export const NARROW_QUERY = '(max-width: 767px)';

export function isNarrowViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.(NARROW_QUERY).matches === true;
}

/** On narrow screens the rail always starts collapsed and an expand there is
 *  never persisted (it overlays the content, so a reload must not reopen it),
 *  which also keeps a desktop preference from forcing the wide rail onto a phone. */
export function useSidebarCollapse(
  storageKey: string = SIDEBAR_COLLAPSED_KEY,
  { collapseOnNarrow = false }: { collapseOnNarrow?: boolean } = {},
): { collapsed: boolean; toggleCollapsed: () => void } {
  const [narrow, setNarrow] = useState(() => collapseOnNarrow && isNarrowViewport());
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (narrow) return true;
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(storageKey) === '1';
  });

  // A crossing collapses on the way in and restores the wide preference on the
  // way out; an expand made while narrow lasts only until the next crossing.
  useEffect(() => {
    if (!collapseOnNarrow || typeof window === 'undefined') return;
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(NARROW_QUERY);
    const onChange = (event: { matches: boolean }) => {
      setNarrow(event.matches);
      if (event.matches) {
        setCollapsed(true);
      } else {
        setCollapsed(window.localStorage.getItem(storageKey) === '1');
      }
    };
    // Older Safari's MediaQueryList has addListener only.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
  }, [collapseOnNarrow, storageKey]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      if (!narrow && typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, next ? '1' : '0');
      }
      return next;
    });
  }, [narrow, storageKey]);

  return { collapsed, toggleCollapsed };
}
